import {
  assetArches,
  assetKindLabels,
  assetKinds,
  assetOses,
  saveReleaseSchema,
  type Release,
  type ReleaseAssetInput,
  type SaveReleaseInput,
} from '@nexo/types';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  IconButton,
  Input,
  Select,
  Sheet,
  Textarea,
  useToast,
} from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Monitor, Package, Plus, Store, Trash2, X } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatLongDate } from '../../lib/format';
import { AdminPage, QueryState } from './AdminLayout';
import { errorMessage } from '../../lib/forms';

const osLabels = { linux: 'Linux', windows: 'Windows', macos: 'macOS' } as const;

type Draft = { id: string | null } & Required<Omit<SaveReleaseInput, 'assets'>> & {
    assets: ReleaseAssetInput[];
  };

const emptyDraft = (): Draft => ({
  id: null,
  version: '',
  channel: 'stable',
  notes: '',
  mandatory: false,
  published: false,
  assets: [],
});

const storePresets: { label: string; asset: ReleaseAssetInput }[] = [
  {
    label: 'Snap Store',
    asset: {
      os: 'linux',
      arch: 'x86_64',
      kind: 'store',
      label: 'Snap Store',
      url: 'https://snapcraft.io/nexo-hub',
    },
  },
  {
    label: 'Microsoft Store',
    asset: {
      os: 'windows',
      arch: 'x86_64',
      kind: 'store',
      label: 'Microsoft Store',
      url: 'https://apps.microsoft.com/',
    },
  },
  {
    label: 'Mac App Store',
    asset: {
      os: 'macos',
      arch: 'universal',
      kind: 'store',
      label: 'Mac App Store',
      url: 'https://apps.apple.com/',
    },
  },
];

export function AdminReleases() {
  const { t } = useTranslation();
  const releases = useQuery({ queryKey: ['admin', 'releases'], queryFn: api.admin.releases });
  const [draft, setDraft] = useState<Draft | null>(null);

  const edit = (release: Release) =>
    setDraft({
      id: release.id,
      version: release.version,
      channel: release.channel,
      notes: release.notes,
      mandatory: release.mandatory,
      published: release.publishedAt !== null,
      assets: release.assets.map(({ id: _id, ...asset }) => asset),
    });

  return (
    <AdminPage
      title={t('Versiones')}
      description={t(
        'Versiones de la app de escritorio: alimentan la página de descargas, el aviso de actualización y el actualizador automático.',
      )}
      actions={
        <Button icon={<Plus className="size-4" />} onClick={() => setDraft(emptyDraft())}>
          {t('Nueva versión')}
        </Button>
      }
    >
      {!releases.data ? (
        <QueryState error={releases.error} onRetry={() => void releases.refetch()} />
      ) : releases.data.length === 0 ? (
        <EmptyState
          icon={<Package className="size-8" />}
          title={t('Aún no hay versiones')}
          description={t(
            'Crea la primera cuando tengas los instaladores del workflow «Desktop builds» o el enlace de la tienda.',
          )}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {releases.data.map((release) => (
            <li key={release.id}>
              <button
                type="button"
                onClick={() => edit(release)}
                className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-border p-4 text-left transition-colors hover:border-border-strong hover:bg-surface-2/40"
              >
                <span className="text-lg font-semibold tabular-nums">v{release.version}</span>
                <Badge tone={release.channel === 'stable' ? 'neutral' : 'warning'}>
                  {release.channel === 'stable' ? t('Estable') : t('Beta')}
                </Badge>
                {release.publishedAt ? (
                  <Badge tone="success">{t('Publicada')}</Badge>
                ) : (
                  <Badge>{t('Borrador')}</Badge>
                )}
                {release.mandatory && <Badge tone="danger">{t('Obligatoria')}</Badge>}
                <span className="flex flex-1 flex-wrap gap-1.5">
                  {assetOses
                    .filter((os) => release.assets.some((a) => a.os === os))
                    .map((os) => (
                      <Badge key={os}>
                        <Monitor className="size-3" /> {osLabels[os]} ·{' '}
                        {release.assets.filter((a) => a.os === os).length}
                      </Badge>
                    ))}
                </span>
                <span className="text-xs text-muted">
                  {release.publishedAt
                    ? t('Publicada el {{date}}', { date: formatLongDate(release.publishedAt) })
                    : t('Creada el {{date}}', { date: formatLongDate(release.createdAt) })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <ReleaseEditor draft={draft} onClose={() => setDraft(null)} />
    </AdminPage>
  );
}

function ReleaseEditor({ draft, onClose }: { draft: Draft | null; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Sheet
      open={draft !== null}
      onClose={onClose}
      label={t('Editar versión')}
      className="sm:max-w-2xl"
    >
      {draft && <EditorForm key={draft.id ?? 'new'} initial={draft} onClose={onClose} />}
    </Sheet>
  );
}

function EditorForm({ initial, onClose }: { initial: Draft; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft>(initial);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const setAsset = (index: number, patch: Partial<ReleaseAssetInput>) =>
    set(
      'assets',
      draft.assets.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    );

  const done = (message: string) => {
    toast({ title: message, tone: 'success' });
    void queryClient.invalidateQueries({ queryKey: ['admin'] });
    onClose();
  };
  const save = useMutation({
    mutationFn: (input: SaveReleaseInput) =>
      draft.id ? api.admin.updateRelease(draft.id, input) : api.admin.createRelease(input),
    onSuccess: (release) => done(t('Versión {{version}} guardada', { version: release.version })),
    onError: (err) => setError(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api.admin.deleteRelease(draft.id ?? ''),
    onSuccess: () => done(t('Versión eliminada')),
    onError: (err) => setError(errorMessage(err)),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { id: _id, ...input } = draft;
    const cleaned = {
      ...input,
      assets: input.assets.map((a) => ({
        ...a,
        label: a.label || null,
        signature: a.signature || null,
      })),
    };
    const parsed = saveReleaseSchema.safeParse(cleaned);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(`${issue?.path.join('.') ?? ''}: ${issue?.message ?? t('Datos no válidos')}`);
      return;
    }
    save.mutate(cleaned);
  }

  return (
    <form onSubmit={onSubmit} className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-6">
        <h2 className="flex-1 text-[15px] font-semibold">
          {draft.id ? t('Versión {{version}}', { version: initial.version }) : t('Nueva versión')}
        </h2>
        {draft.id && (
          <IconButton
            label={t('Eliminar versión')}
            className="hover:text-danger"
            onClick={() => {
              if (window.confirm(t('¿Eliminar esta versión? Dejará de ofrecerse para descargar.')))
                remove.mutate();
            }}
          >
            <Trash2 />
          </IconButton>
        )}
        <IconButton label={t('Cerrar')} autoFocus onClick={onClose}>
          <X />
        </IconButton>
      </header>

      <div className="scrollbar-thin flex-1 space-y-5 overflow-y-auto px-6 py-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('Versión')} hint={t('Formato 1.2.3 (igual que en tauri.conf.json)')}>
            {(props) => (
              <Input
                {...props}
                value={draft.version}
                placeholder="0.2.0"
                onChange={(e) => set('version', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('Canal')}>
            {(props) => (
              <Select
                {...props}
                value={draft.channel}
                onChange={(channel) => set('channel', channel)}
                options={[
                  { value: 'stable', label: t('Estable') },
                  { value: 'beta', label: t('Beta') },
                ]}
              />
            )}
          </Field>
        </div>
        <Field
          label={t('Notas de la versión')}
          hint={t('Se muestran en la página de descargas y en el aviso de actualización.')}
        >
          {(props) => (
            <Textarea
              {...props}
              rows={5}
              value={draft.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder={`${t('- Nuevo: …')}\n${t('- Corregido: …')}`}
            />
          )}
        </Field>
        <div className="flex flex-wrap gap-6">
          <Toggle
            label={t('Publicada')}
            hint={t('Visible para los usuarios')}
            checked={draft.published}
            onChange={(v) => set('published', v)}
          />
          <Toggle
            label={t('Obligatoria')}
            hint={t('La app insistirá en actualizar')}
            checked={draft.mandatory}
            onChange={(v) => set('mandatory', v)}
          />
        </div>

        <section>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h3 className="flex-1 text-sm font-semibold">{t('Descargas y tiendas')}</h3>
            {storePresets.map((preset) => (
              <Button
                key={preset.label}
                size="sm"
                variant="secondary"
                icon={<Store className="size-3.5" />}
                onClick={() => set('assets', [...draft.assets, { ...preset.asset }])}
              >
                {preset.label}
              </Button>
            ))}
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="size-3.5" />}
              onClick={() =>
                set('assets', [
                  ...draft.assets,
                  { os: 'linux', arch: 'x86_64', kind: 'appimage', url: '' },
                ])
              }
            >
              {t('Archivo')}
            </Button>
          </div>
          {draft.assets.length === 0 ? (
            <p className="rounded-xl border border-dashed border-border-strong px-4 py-6 text-center text-sm text-subtle">
              {t(
                'Añade las URLs de los instaladores (.deb, .AppImage, .msi, .dmg…) o los enlaces de las tiendas.',
              )}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {draft.assets.map((asset, index) => (
                <li key={index} className="rounded-xl border border-border bg-surface-2/40 p-3">
                  <div className="flex flex-wrap gap-2">
                    <Select
                      aria-label={t('Sistema')}
                      value={asset.os}
                      onChange={(os) => setAsset(index, { os })}
                      size="sm"
                      className="w-32!"
                      options={assetOses.map((os) => ({ value: os, label: osLabels[os] }))}
                    />
                    <Select
                      aria-label={t('Arquitectura')}
                      value={asset.arch}
                      onChange={(arch) => setAsset(index, { arch })}
                      size="sm"
                      className="w-32!"
                      options={assetArches.map((arch) => ({ value: arch, label: arch }))}
                    />
                    <Select
                      aria-label={t('Tipo')}
                      value={asset.kind}
                      onChange={(kind) => setAsset(index, { kind })}
                      size="sm"
                      className="w-56!"
                      options={assetKinds.map((kind) => ({
                        value: kind,
                        label: t(assetKindLabels[kind]),
                      }))}
                    />
                    <span className="flex-1" />
                    <IconButton
                      label={t('Quitar')}
                      size="sm"
                      onClick={() =>
                        set(
                          'assets',
                          draft.assets.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <Trash2 />
                    </IconButton>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Input
                      aria-label="URL"
                      value={asset.url}
                      placeholder="https://…"
                      onChange={(e) => setAsset(index, { url: e.target.value })}
                      className="h-8 font-mono text-xs"
                    />
                    {asset.url && (
                      <a
                        href={asset.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-surface-2"
                        title={t('Abrir')}
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    )}
                  </div>
                  {asset.kind === 'store' ? (
                    <Input
                      aria-label={t('Nombre de la tienda')}
                      value={asset.label ?? ''}
                      placeholder={t('Nombre de la tienda')}
                      onChange={(e) => setAsset(index, { label: e.target.value })}
                      className="mt-2 h-8 text-xs"
                    />
                  ) : (
                    <Input
                      aria-label={t('Firma del actualizador')}
                      value={asset.signature ?? ''}
                      placeholder={t('Firma .sig (solo para el actualizador automático, opcional)')}
                      onChange={(e) => setAsset(index, { signature: e.target.value })}
                      className="mt-2 h-8 font-mono text-xs"
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
        {error && (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </p>
        )}
      </div>

      <footer className="flex justify-end gap-2 border-t border-border px-6 py-3">
        <Button variant="ghost" onClick={onClose}>
          {t('Cancelar')}
        </Button>
        <Button type="submit" loading={save.isPending}>
          {t('Guardar')}
        </Button>
      </footer>
    </form>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 accent-[var(--color-accent)]"
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted">{hint}</span>
      </span>
    </label>
  );
}
